import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import GuideLead from '../components/GuideLead';
import GuideCTA from '../components/GuideCTA';

const GuideAnalyseCV = () => {
    return (
        <div className="min-h-screen bg-slate-200">
            <Helmet>
                <title>Analyse de CV par IA: guide complet (exemples et conseils)</title>
                <meta name="description" content="Apprenez à analyser votre CV avec l'IA: critères clés, erreurs fréquentes, exemples et check-list pour améliorer votre score et passer les ATS." />
                <link rel="canonical" href="https://www.cv-analyse.fr/guide/analyse-cv-ia" />
                <script type="application/ld+json">{`
                {
                  "@context": "https://schema.org",
                  "@type": "BreadcrumbList",
                  "itemListElement": [
                    {"@type": "ListItem","position": 1,"name": "Accueil","item": "https://www.cv-analyse.fr/"},
                    {"@type": "ListItem","position": 2,"name": "Analyse CV IA","item": "https://www.cv-analyse.fr/guide/analyse-cv-ia"}
                  ]
                }
                `}</script>
                <script type="application/ld+json">{`
                {
                  "@context": "https://schema.org",
                  "@type": "Article",
                  "headline": "Analyse de CV par IA: guide complet",
                  "description": "Apprenez à analyser votre CV avec l'IA",
                  "author": {"@type": "Organization", "name": "CV Analyse", "alternateName": "CV Analyzer"},
                  "publisher": {"@type": "Organization", "name": "CV Analyse", "alternateName": "CV Analyzer"},
                  "dateModified": "2026-08-15",
                  "mainEntityOfPage": {"@type": "WebPage", "@id": "https://www.cv-analyse.fr/guide/analyse-cv-ia"}
                }
                `}</script>
                <script type="application/ld+json">{`
                {
                  "@context": "https://schema.org",
                  "@type": "HowTo",
                  "name": "Comment analyser son CV avec l'IA",
                  "description": "Utilisez l'intelligence artificielle pour obtenir une analyse complète de votre CV et améliorer vos chances d'obtenir un entretien.",
                  "step": [
                    {"@type": "HowToStep", "name": "Télécharger son CV en PDF", "text": "Déposez votre CV au format PDF sur la plateforme CV Analyse."},
                    {"@type": "HowToStep", "name": "Lancer l'analyse IA", "text": "L'IA analyse automatiquement structure, mots-clés, formatage et contenu."},
                    {"@type": "HowToStep", "name": "Lire le rapport de score", "text": "Consultez votre score global et le détail par critère : forme, fond, ATS, lisibilité."},
                    {"@type": "HowToStep", "name": "Corriger les points faibles", "text": "Suivez les recommandations personnalisées pour chaque section de votre CV."},
                    {"@type": "HowToStep", "name": "Relancer l'analyse pour valider", "text": "Retestez votre CV après corrections pour mesurer la progression de votre score."}
                  ]
                }
                `}</script>
            </Helmet>

            {/* Fil d'Ariane */}
            <nav aria-label="Fil d'Ariane" className="pt-16 pb-4">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <ol className="flex items-center space-x-2 text-sm text-slate-600" itemScope itemType="https://schema.org/BreadcrumbList">
                        <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                            <Link to="/" itemProp="item" className="hover:text-slate-900">
                                <span itemProp="name">Accueil</span>
                            </Link>
                            <meta itemProp="position" content="1" />
                        </li>
                        <li className="text-slate-400">/</li>
                        <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                            <span itemProp="name" className="text-slate-900">Analyse CV IA</span>
                            <meta itemProp="position" content="2" />
                        </li>
                    </ol>
                </div>
            </nav>

            {/* Hero Section */}
            <div className="bg-slate-900 pt-4 pb-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 rounded-3xl mb-6">
                            <span className="text-4xl">🤖</span>
                        </div>
                        <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                            Analyse de CV par IA
                </h1>
                        <p className="text-xl md:text-2xl text-white max-w-3xl mx-auto leading-relaxed font-medium">
                            Guide complet pour comprendre et optimiser votre CV avec l'intelligence artificielle
                        </p>
                    </div>
                </div>
            </div>

            <GuideLead slug="analyse-cv-ia" />

            {/* Table des matières */}
            <div className="card-surface mx-4 mt-4 max-w-4xl mx-auto">
                <div className="p-6">
                    <h2 className="text-xl font-bold text-gray-900 mb-4">📋 Table des matières</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <a href="#criteres" className="flex items-center p-3 rounded-xl hover:bg-blue-50 transition-colors">
                            <span className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3 text-blue-600 font-bold">1</span>
                            <span className="text-gray-700">Critères d'évaluation IA</span>
                        </a>
                        <a href="#erreurs" className="flex items-center p-3 rounded-xl hover:bg-blue-50 transition-colors">
                            <span className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3 text-blue-600 font-bold">2</span>
                            <span className="text-gray-700">Erreurs fréquentes</span>
                        </a>
                        <a href="#checklist" className="flex items-center p-3 rounded-xl hover:bg-blue-50 transition-colors">
                            <span className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3 text-blue-600 font-bold">3</span>
                            <span className="text-gray-700">Check-list rapide</span>
                        </a>
                        <a href="#exemples" className="flex items-center p-3 rounded-xl hover:bg-blue-50 transition-colors">
                            <span className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3 text-blue-600 font-bold">4</span>
                            <span className="text-gray-700">Exemples concrets</span>
                        </a>
                    </div>
                </div>
            </div>

            <article className="px-6 py-16 mx-auto max-w-5xl">

                {/* Section 1: Critères d'évaluation */}
                <div id="criteres" className="mb-16">
                    <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
                        <div className="flex items-center mb-6">
                            <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mr-4">
                                <span className="text-2xl">🎯</span>
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900">Les critères clés analysés par l'IA</h2>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-2xl p-6">
                                <h3 className="text-xl font-bold text-blue-900 mb-3">🔍 Mots-clés pertinents</h3>
                                <p className="text-gray-700">L'IA analyse la correspondance entre les mots-clés de votre CV et ceux du poste visé.</p>
                            </div>
                            <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-2xl p-6">
                                <h3 className="text-xl font-bold text-purple-900 mb-3">📊 Structure claire</h3>
                                <p className="text-gray-700">Hiérarchie des informations et organisation logique des sections.</p>
                            </div>
                            <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-2xl p-6">
                                <h3 className="text-xl font-bold text-green-900 mb-3">📈 Impact mesurable</h3>
                                <p className="text-gray-700">Présence de chiffres, résultats concrets et responsabilités quantifiées.</p>
                            </div>
                            <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-2xl p-6">
                                <h3 className="text-xl font-bold text-orange-900 mb-3">⚡ Compatibilité ATS</h3>
                                <p className="text-gray-700">Formatage, polices et structure compatibles avec les systèmes de recrutement.</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Section 2: Erreurs fréquentes */}
                <div id="erreurs" className="mb-16">
                    <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
                        <div className="flex items-center mb-6">
                            <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-red-600 rounded-2xl flex items-center justify-center mr-4">
                                <span className="text-2xl">⚠️</span>
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900">Erreurs fréquentes à éviter</h2>
                        </div>
                        
                        <div className="space-y-4">
                            <div className="flex items-start p-4 bg-red-50 rounded-xl border-l-4 border-red-500">
                                <span className="text-red-500 text-xl mr-4">❌</span>
                                <div>
                                    <h3 className="font-bold text-red-900">Bloc de texte trop dense</h3>
                                    <p className="text-red-700">Évitez les paragraphes longs sans puces ni structure claire.</p>
                                </div>
                            </div>
                            <div className="flex items-start p-4 bg-red-50 rounded-xl border-l-4 border-red-500">
                                <span className="text-red-500 text-xl mr-4">❌</span>
                                <div>
                                    <h3 className="font-bold text-red-900">Jargon non pertinent</h3>
                                    <p className="text-red-700">Utilisez des termes que les recruteurs comprennent, avec des chiffres concrets.</p>
                                </div>
                            </div>
                            <div className="flex items-start p-4 bg-red-50 rounded-xl border-l-4 border-red-500">
                                <span className="text-red-500 text-xl mr-4">❌</span>
                                <div>
                                    <h3 className="font-bold text-red-900">Fichier non lisible</h3>
                                    <p className="text-red-700">Évitez les PDF scannés ou images qui ne passent pas les filtres ATS.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Section 3: Check-list */}
                <div id="checklist" className="mb-16">
                    <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
                        <div className="flex items-center mb-6">
                            <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-2xl flex items-center justify-center mr-4">
                                <span className="text-2xl">✅</span>
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900">Check-list rapide</h2>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <div className="flex items-center p-4 bg-green-50 rounded-xl">
                                    <span className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mr-4 text-white font-bold">1</span>
                                    <span className="font-semibold text-green-900">Titre clair et précis</span>
                                </div>
                                <div className="flex items-center p-4 bg-green-50 rounded-xl">
                                    <span className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mr-4 text-white font-bold">2</span>
                                    <span className="font-semibold text-green-900">3-5 réalisations chiffrées</span>
                                </div>
                            </div>
                            <div className="space-y-4">
                                <div className="flex items-center p-4 bg-green-50 rounded-xl">
                                    <span className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mr-4 text-white font-bold">3</span>
                                    <span className="font-semibold text-green-900">Mots-clés intégrés</span>
                                </div>
                                <div className="flex items-center p-4 bg-green-50 rounded-xl">
                                    <span className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mr-4 text-white font-bold">4</span>
                                    <span className="font-semibold text-green-900">Formatage simple</span>
                                </div>
                            </div>
                        </div>
                        </div>
                    </div>

                {/* Section 4: Exemples */}
                <div id="exemples" className="mb-16">
                    <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
                        <div className="flex items-center mb-6">
                            <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mr-4">
                                <span className="text-2xl">💡</span>
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900">Exemple d'accomplissement chiffré</h2>
                        </div>
                        
                        <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-6">
                            <div className="flex items-start">
                                <span className="text-2xl mr-4">📝</span>
                                <div>
                                    <h3 className="font-bold text-gray-900 mb-2">Avant (faible impact)</h3>
                                    <p className="text-gray-600 italic mb-4">"Géré les projets de développement et amélioré les processus"</p>
                                    
                                    <h3 className="font-bold text-gray-900 mb-2">Après (fort impact)</h3>
                                    <div className="bg-white p-4 rounded-xl border-l-4 border-green-500">
                                        <p className="text-gray-800 font-medium">"Optimisé le temps de construction CI/CD de 40% en mettant en cache les dépendances et en parallélisant les jobs"</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    </div>

                {/* CTA Section */}
                <GuideCTA
                    title="Votre CV obtient-il le score qu'il mérite ?"
                    description="Notre IA analyse votre CV en 2 minutes et vous donne un score sur 100 avec des suggestions concrètes — score et 3 conseils offerts."
                    ctaText="Analyser mon CV — aperçu offert"
                    theme="blue"
                />

                {/* Navigation entre guides */}
                <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Link to="/guides" className="group flex items-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                        <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mr-4 group-hover:bg-blue-100 transition-colors">
                            <svg className="w-6 h-6 text-gray-600 group-hover:text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-900 group-hover:text-blue-700">Retour aux guides</h3>
                            <p className="text-sm text-gray-600">Voir tous nos guides CV</p>
                        </div>
                    </Link>
                    
                    <Link to="/guide/optimiser-cv-ats" className="group flex items-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                        <div>
                            <h3 className="font-bold text-gray-900 group-hover:text-green-700">Guide suivant</h3>
                            <p className="text-sm text-gray-600">Optimiser son CV pour les ATS</p>
                        </div>
                        <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center ml-4 group-hover:bg-green-100 transition-colors">
                            <svg className="w-6 h-6 text-gray-600 group-hover:text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                            </svg>
                        </div>
                    </Link>
                </div>
            </article>
        </div>
    );
};

export default GuideAnalyseCV;


